import mapboxgl from 'mapbox-gl';
import type { Coordinate } from '@shared/models/geography.model';
import { convertMetricOffsetToCoordinate } from '@shared/helpers/geometry/coordinates.helpers';
import type {
  PointPayload,
  PointDragSnapGuides,
} from '../models/map-observables.models';
import { MAP_THREEJS_LAYER_IDS } from '../helpers/map-config.constants';
import { compileShader, linkProgram } from './gl-program.helpers';

const GUIDE_EXTENT_METERS = 10000;

/** Straight guides intersect exactly at the pickup point in its constant Mercator height plane. */
export const buildPointDragGuideVertices = (input: {
  guides: PointDragSnapGuides;
  location: Coordinate;
  elevation: number;
}): Float32Array => {
  const worldOrigin = mapboxgl.MercatorCoordinate.fromLngLat(input.location);
  const anchor = mapboxgl.MercatorCoordinate.fromLngLat(
    convertMetricOffsetToCoordinate(input.location, input.guides.origin),
    input.elevation,
  );
  const x = anchor.x - worldOrigin.x;
  const y = anchor.y - worldOrigin.y;
  const z = anchor.z - worldOrigin.z;
  const extent = GUIDE_EXTENT_METERS * anchor.meterInMercatorCoordinateUnits();
  const vertices: number[] = [];
  for (const [dx, dy] of input.guides.directions) {
    vertices.push(
      x - dx * extent,
      y - dy * extent,
      z,
      x + dx * extent,
      y + dy * extent,
      z,
    );
  }
  return new Float32Array(vertices);
};

/** Solid guides share the model height plane and overlay building geometry. */
export const createPointDragGuidesLayer = (input: {
  getPointMove: () =>
    Pick<PointPayload, 'pointDragSnapGuides' | 'elevation'> | undefined;
  getLocation: () => Coordinate;
  getProjection: (matrix: readonly number[]) => Float32Array;
}): mapboxgl.CustomLayerInterface => {
  let resources:
    | {
        program: WebGLProgram;
        buffer: WebGLBuffer;
        position: number;
        matrix: WebGLUniformLocation | null;
      }
    | undefined;
  return {
    id: MAP_THREEJS_LAYER_IDS.pointDragGuides,
    type: 'custom',
    renderingMode: '2d',
    onAdd: (_map, gl) => {
      const vertex = compileShader(
        gl,
        gl.VERTEX_SHADER,
        `
        precision highp float;
        attribute vec3 a_position;
        uniform mat4 u_matrix;
        void main() {
          gl_Position = u_matrix * vec4(a_position, 1.0);
        }
      `,
      );
      const fragment = compileShader(
        gl,
        gl.FRAGMENT_SHADER,
        `
        precision highp float;
        void main() {
          gl_FragColor = vec4(1.0, 0.0, 1.0, 0.7);
        }
      `,
      );
      const program = linkProgram(gl, vertex, fragment);
      const buffer = gl.createBuffer();
      if (!buffer) {
        gl.deleteProgram(program);
        throw new Error('Failed to create point guide buffer');
      }
      resources = {
        program,
        buffer,
        position: gl.getAttribLocation(program, 'a_position'),
        matrix: gl.getUniformLocation(program, 'u_matrix'),
      };
    },
    render: (gl, matrix) => {
      const payload = input.getPointMove();
      const guides = payload?.pointDragSnapGuides;
      if (!resources || !guides) return;
      const location = input.getLocation();
      const vertices = buildPointDragGuideVertices({
        guides,
        location,
        elevation: payload.elevation ?? 0,
      });
      const depthTest = gl.isEnabled(gl.DEPTH_TEST);
      const depthWrite = gl.getParameter(gl.DEPTH_WRITEMASK);
      gl.useProgram(resources.program);
      gl.bindBuffer(gl.ARRAY_BUFFER, resources.buffer);
      gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.DYNAMIC_DRAW);
      gl.enableVertexAttribArray(resources.position);
      gl.vertexAttribPointer(resources.position, 3, gl.FLOAT, false, 12, 0);
      gl.uniformMatrix4fv(resources.matrix, false, input.getProjection(matrix));
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.disable(gl.DEPTH_TEST);
      gl.depthMask(false);
      gl.lineWidth(window.devicePixelRatio || 1);
      gl.drawArrays(gl.LINES, 0, vertices.length / 3);
      gl.depthMask(depthWrite);
      if (depthTest) gl.enable(gl.DEPTH_TEST);
    },
    onRemove: (_map, gl) => {
      if (!resources) return;
      gl.deleteBuffer(resources.buffer);
      gl.deleteProgram(resources.program);
      resources = undefined;
    },
  };
};
